import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindContactsByCreatedByIdQueryResponse,
  FindContactsByCreatedByIdQueryParams,
} from "../types/FindContactsByCreatedById"

type FindContactsByCreatedByIdClient = typeof client<
  FindContactsByCreatedByIdQueryResponse,
  Error,
  never
>
type FindContactsByCreatedById = {
  data: FindContactsByCreatedByIdQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindContactsByCreatedByIdQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindContactsByCreatedByIdClient>>
  client: {
    parameters: Partial<Parameters<FindContactsByCreatedByIdClient>[0]>
    return: Awaited<ReturnType<FindContactsByCreatedByIdClient>>
  }
}
export const findContactsByCreatedByIdQueryKey = (
  params: FindContactsByCreatedById["queryParams"]
) => [{ url: "/v1/contacts" }, ...(params ? [params] : [])] as const
export type FindContactsByCreatedByIdQueryKey = ReturnType<
  typeof findContactsByCreatedByIdQueryKey
>
export function findContactsByCreatedByIdQueryOptions<
  TData = FindContactsByCreatedById["response"],
  TQueryData = FindContactsByCreatedById["response"],
>(
  params: FindContactsByCreatedById["queryParams"],
  options: FindContactsByCreatedById["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindContactsByCreatedById["response"],
    FindContactsByCreatedById["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findContactsByCreatedByIdQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindContactsByCreatedById["data"],
        FindContactsByCreatedById["error"]
      >({
        method: "get",
        url: `/v1/contacts`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find contacts by created by id
 * @link /v1/contacts
 */
export function useFindContactsByCreatedById<
  TData = FindContactsByCreatedById["response"],
  TQueryData = FindContactsByCreatedById["response"],
  TQueryKey extends QueryKey = FindContactsByCreatedByIdQueryKey,
>(
  params: FindContactsByCreatedById["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindContactsByCreatedById["response"],
        FindContactsByCreatedById["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindContactsByCreatedById["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindContactsByCreatedById["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findContactsByCreatedByIdQueryKey(params)
  const query = useQuery<
    FindContactsByCreatedById["data"],
    FindContactsByCreatedById["error"],
    TData,
    any
  >({
    ...findContactsByCreatedByIdQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindContactsByCreatedById["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
